import { useMemo } from 'react';
import { createSearchParams } from 'react-router-dom';

// third-party
import { FormattedMessage } from 'react-intl';

// assets
import {
  DashboardOutlined,
  GoldOutlined,
  HomeOutlined,
  UserOutlined,
  SettingOutlined,
  ExperimentOutlined,
  FileOutlined,
  HddOutlined,
  EyeOutlined,
  ReadOutlined,
  ApiOutlined,
  GroupOutlined,
  AlertOutlined,
  CalendarOutlined,
  LinkOutlined,
  BankOutlined,
  ApartmentOutlined,
  ToolOutlined,
  IdcardOutlined
} from '@ant-design/icons';

// type
import { NavItemType } from 'types/menu';

// config
import useDynamicConfig from 'hooks/useDynamicConfig';

// hooks
import useUserRole from 'hooks/useUserRole';
import useSkipCompanyQuery from 'hooks/useSkipCompanyQuery';

// store
import { useGetCompanyQuery } from 'store/reducers/api/company';

// icons
const icons = {
  DashboardOutlined,
  GoldOutlined,
  HomeOutlined,
  UserOutlined,
  SettingOutlined,
  ExperimentOutlined,
  FileOutlined,
  HddOutlined,
  EyeOutlined,
  ReadOutlined,
  ApiOutlined,
  GroupOutlined,
  AlertOutlined,
  CalendarOutlined,
  LinkOutlined,
  BankOutlined,
  ApartmentOutlined,
  ToolOutlined,
  IdcardOutlined
};

// ==============================|| MENU ITEMS - DASHBOARD ||============================== //

const useDashboardItems = (): NavItemType => {
  const { DEFAULT_MM_FILTER, DEFAULT_ALERT_FILTER } = useDynamicConfig();
  const { hasSystemRole, hasCompanyRole, hasPropertyRole, activeRole } = useUserRole();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const { data: userCompany } = useGetCompanyQuery(activeRole?.company_id!, { skip: skipCompanyQuery });

  const dashboardItems: NavItemType = useMemo(() => {
    const isBillingCompany = userCompany?.company_type_id === 3;

    const companiesTitleId = hasSystemRole ? 'companies' : hasCompanyRole ? 'properties' : hasPropertyRole ? 'buildings' : '';

    const companiesUrl = hasSystemRole
      ? '/companies'
      : hasCompanyRole
      ? `/companies/${activeRole?.company_id}/properties`
      : hasPropertyRole
      ? `/companies/${activeRole?.company_id}/properties/${activeRole?.property_id}/buildings`
      : undefined;

    const companiesIcon = hasSystemRole ? icons.GoldOutlined : icons.GroupOutlined;

    return {
      id: 'group-dashboard',
      type: 'group',
      children: [
        ...((hasCompanyRole || hasPropertyRole) && !isBillingCompany
          ? [
              {
                id: 'dashboard',
                title: <FormattedMessage id="dashboard" />,
                type: 'item',
                url: '/dashboard',
                icon: icons.HomeOutlined
              }
            ]
          : []),
        {
          id: 'companies',
          title: <FormattedMessage id={companiesTitleId} />,
          type: 'list',
          url: companiesUrl,
          icon: companiesIcon
        },
        ...(hasSystemRole
          ? [
              {
                id: 'property-owners',
                title: <FormattedMessage id="property-owners" />,
                type: 'item',
                url: '/property-owners',
                icon: icons.IdcardOutlined
              }
            ]
          : []),
        {
          id: 'properties',
          title: <FormattedMessage id="properties" />,
          type: 'list',
          url: '/properties',
          icon: icons.GroupOutlined
        },
        {
          id: 'buildings',
          title: <FormattedMessage id="buildings" />,
          type: 'list',
          url: '/buildings',
          icon: icons.BankOutlined
        },
        {
          id: 'alerts',
          title: <FormattedMessage id="alerts" />,
          type: 'list',
          url: '/alerts',
          icon: icons.AlertOutlined,
          search: createSearchParams(DEFAULT_ALERT_FILTER).toString()
        },
        ...(isBillingCompany
          ? []
          : [
              {
                id: 'gateways',
                title: <FormattedMessage id="gateways" />,
                type: 'item',
                url: '/gateways',
                icon: icons.HddOutlined
              }
            ]),
        {
          id: 'appliances',
          title: <FormattedMessage id="fixtures" />,
          type: 'item',
          url: '/appliances',
          icon: icons.ApiOutlined
        },
        {
          id: 'submissions',
          title: <FormattedMessage id="submissions" />,
          type: 'collapse',
          icon: icons.ReadOutlined,
          children: [
            {
              id: 'submissions-unassigned',
              title: <FormattedMessage id="unassigned" />,
              type: 'item',
              url: '/submissions/unassigned'
            },
            {
              id: 'keep-alive',
              title: <FormattedMessage id="keep-alive" />,
              type: 'item',
              url: '/submissions/keep-alive'
            },
            {
              id: 'parsed-old',
              title: <FormattedMessage id="parsed-old" />,
              type: 'item',
              url: '/submissions/parsed-old'
            }
          ]
        },
        {
          id: 'micrometers',
          title: <FormattedMessage id="micrometers" />,
          type: 'collapse',
          icon: icons.EyeOutlined,
          children: [
            {
              id: 'micrometers-assigned',
              title: <FormattedMessage id="assigned" />,
              type: 'item',
              url: '/micrometers/assigned',
              search: createSearchParams({
                ...DEFAULT_MM_FILTER
              }).toString()
            },
            {
              id: 'micrometers-unassigned',
              title: <FormattedMessage id="unassigned" />,
              type: 'item',
              url: '/micrometers/unassigned'
            },
            {
              id: 'micrometers-excluded',
              title: <FormattedMessage id="excluded" />,
              type: 'item',
              url: '/micrometers/excluded'
            },
            {
              id: 'assignment',
              title: <FormattedMessage id="assignment" />,
              type: 'item',
              url: '/micrometers/assignment'
            },
            {
              id: 'config',
              title: <FormattedMessage id="config" />,
              type: 'item',
              url: '/micrometers/config'
            },
            {
              id: 'defaults',
              title: <FormattedMessage id="defaults" />,
              type: 'item',
              url: '/micrometers/defaults'
            },
            {
              id: 'testing',
              title: <FormattedMessage id="testing" />,
              type: 'item',
              url: '/micrometers/testing'
            },
            {
              id: 'downlink',
              title: <FormattedMessage id="downlink" />,
              type: 'item',
              url: '/micrometers/downlink'
            },
            {
              id: 'flume-accounts',
              title: <FormattedMessage id="flume" />,
              type: 'item',
              url: '/flume-accounts'
            }
          ]
        },
        ...(isBillingCompany
          ? []
          : [
              {
                id: 'users',
                title: <FormattedMessage id="users" />,
                type: 'item',
                url: '/users',
                icon: icons.UserOutlined
              }
            ]),
        ...(hasSystemRole
          ? [
              {
                id: 'utility-companies',
                title: <FormattedMessage id="utility-companies" />,
                type: 'item',
                url: '/utility-companies',
                icon: icons.ApartmentOutlined
              }
            ]
          : []),
        {
          id: 'settings',
          title: <FormattedMessage id="settings" />,
          type: 'item',
          url: '/settings',
          icon: icons.SettingOutlined
        },
        {
          id: 'events',
          title: <FormattedMessage id="events" />,
          type: 'item',
          url: '/events',
          icon: icons.CalendarOutlined
        },
        ...(hasSystemRole
          ? [
              {
                id: 'operations',
                title: <FormattedMessage id="operations" />,
                type: 'item',
                url: '/operations',
                icon: icons.ToolOutlined
              }
            ]
          : []),
        ...(hasSystemRole
          ? [
              {
                id: 'overview',
                title: <FormattedMessage id="overview" />,
                type: 'item',
                url: '/overview',
                icon: icons.DashboardOutlined
              }
            ]
          : [])
        /*,
      {
        id: 'reports',
        title: <FormattedMessage id="reports" />,
        type: 'item',
        url: '/reports',
        icon: icons.FileOutlined
      },
      {
        id: 'settings',
        title: <FormattedMessage id="settings" />,
        type: 'item',
        url: '/settings',
        icon: icons.SettingOutlined
      }*/
      ]
    };
  }, [DEFAULT_MM_FILTER, hasCompanyRole, hasSystemRole, hasPropertyRole, activeRole, userCompany, DEFAULT_ALERT_FILTER]);
  return dashboardItems;
};

export default useDashboardItems;
